<template>
  <el-dialog
  
    :visible.sync="showDialog"
    width="600px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    
  >
    <template slot="title">
      <span class="font-semibold flex justify-center">Just a few questions...</span>
    </template>

    <div class="mb-4">
      <p>
        Please check or set the selections below so that we can make sure your records are up-to-date before we launch some new Hera features!
      </p>
    </div>

    <el-form :model="form" :rules="rules" ref="userRoleForm" label-position="top">
      <el-form-item label="Users who are dispatchers" prop="dispatchers">
        <el-select v-model="form.dispatchers" multiple filterable placeholder="Select users" class="w-full">
          <el-option
            v-for="user in users"
            :key="user.id"
            :label="`${user.firstName} ${user.lastName} ${phoneFormatter(user.phone)}`"
            :value="user.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="Users who are HR managers" prop="hrManagers">
        <el-select v-model="form.hrManagers" multiple filterable placeholder="Select users" class="w-full">
          <el-option
            v-for="user in users"
            :key="user.id + '-hr'"
            :label="`${user.firstName} ${user.lastName} ${phoneFormatter(user.phone)}`"
            :value="user.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="Users who should receive Hera billing emails" prop="billingContacts">
        <el-select v-model="form.billingContacts" multiple filterable placeholder="Select users" class="w-full">
          <el-option
            v-for="user in users"
            :key="user.id + '-billing'"
            :label="`${user.firstName} ${user.lastName} (${user.email})`"
            :value="user.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="Your payroll provider" prop="payrollProvider">
        <el-select v-model="form.payrollProvider" placeholder="Select one" class="w-full">
          <el-option
            v-for="(item, index) in payrollProviderOptions" :key="index"
            :label="item.label"
            :value="item.value">
          </el-option>
        </el-select>
      </el-form-item>
    </el-form>

    <template slot="footer">
      <el-button type="info" v-loading="buttonLoading" :disabled="!isFormComplete" @click="onConfirm">Checked & confirmed</el-button>
      <el-button type="primary" @click="onLater">I'll do this later</el-button>
    </template>

  </el-dialog>
</template>

<script>
import { API } from 'aws-amplify';
import { updateTenantDetail } from '@/store/mutations';
import { TENANT_PAYROLL_PROVIDER_OPTIONS } from '@/views/System/Tenants/constants.js';
import { formatPhoneNumber } from '@/utilities/phoneNumberFormat';
import { getUserRolesByTenantId, getUserRoleLinksByUserRoleId } from '@/views/Settings/Users/utils';

export default {
  name: "UserRoleDialog",
  props: {
    userInfo: Object,
    tenant: Object,
    users: Array, // [{ id, name, phone, email }]
    //payrollProviders: Array, // ['ADP', 'PAYCOM', ...]
  },
  data() {
    return {
      showDialog: false,
      buttonLoading: false,
      form: {
        dispatchers: [],
        hrManagers: [],
        billingContacts: [],
        payrollProvider: null,
      },
      userRoles: [],
      rules: {
        dispatchers: [
          { required: true, message: 'Please select at least one dispatcher', trigger: 'change' },
        ],
        hrManagers: [
          { required: true, message: 'Please select at least one HR manager', trigger: 'change' },
        ],
        billingContacts: [
          { required: true, message: 'Please select at least one billing contact', trigger: 'change' },
        ],
        payrollProvider: [
          { required: true, message: 'Please select a payroll provider', trigger: 'change' },
        ],
      },
    };
  },
  computed: {
    isFormComplete() {
      return (
        this.form.dispatchers.length > 0 &&
        this.form.hrManagers.length > 0 &&
        this.form.billingContacts.length > 0 &&
        !!this.form.payrollProvider
      );
    },
    payrollProviderOptions(){
      return TENANT_PAYROLL_PROVIDER_OPTIONS
    }
  },
  async mounted() {
    const fromLogin = this.$route.query.fromLogin
    if (!!fromLogin && !!this.userInfo?.permissionFullAccess &&
      !this.tenant.customerCheckedUserRolesDateTime
    ) {
      await this.loadData()
      this.showDialog = true
    }
    if (this.$route.query.fromLogin) {
      const query = { ...this.$route.query }
      delete query.fromLogin
      this.$router.replace({ query }).catch(() => {})
    }
  },
  methods: {

    async loadData(){
      const tenantId = this.tenant.id
      this.userRoles = await getUserRolesByTenantId(tenantId)

      // Auto-create default roles if they don't exist yet (new tenant scenario)
      if (!this.userRoles.length) {
        try {
          await safeFunction(API.post)('rds', '/userRole/createUserRoles', { body: { tenantId } })
          this.userRoles = await getUserRolesByTenantId(tenantId)
        } catch (e) {
          console.error('Error creating user roles', e)
        }
      }
     
      const dispatcherRole = this.userRoles.find(item => item.name === "Dispatcher") || {}
      const hrManagerRole = this.userRoles.find(item => item.name === "HR Manager") || {}
      const billingContactRole = this.userRoles.find(item => item.name === "Billing Contact") || {}
      
      const [dispatchers, hrManagers, billingContacts] = await Promise.all([
        dispatcherRole.id ? getUserRoleLinksByUserRoleId(dispatcherRole.id) : Promise.resolve([]),
        hrManagerRole.id ? getUserRoleLinksByUserRoleId(hrManagerRole.id) : Promise.resolve([]),
        billingContactRole.id ? getUserRoleLinksByUserRoleId(billingContactRole.id) : Promise.resolve([])
      ])

      this.form = {
        dispatchers: dispatchers.map(item => item.userId),
        hrManagers: hrManagers.map(item => item.userId),
        billingContacts: billingContacts.map(item => item.userId),
        payrollProvider: this.tenant.payrollProvider,
      }
      
    },

    phoneFormatter(phone){
      return phone ? formatPhoneNumber(phone) : ''
    },

    async onConfirm() {
      try {
        const valid = await new Promise(resolve => {
          this.$refs.userRoleForm.validate(resolve)
        })

        if (!valid) {
          this.displayUserError('Please complete all fields before confirming')
          return
        }

        this.buttonLoading = true
        if (!this.userRoles.length) throw new Error("Something went wrong")
        const req = {
          body: {
            tenantId: this.tenant.id,
            userRoles: this.userRoles,
            dispatchers: this.form.dispatchers,
            hrManagers: this.form.hrManagers,
            billingContacts: this.form.billingContacts,
          }
        }
       
        const response = await safeFunction(API.post)('rds', '/userRole/mutate', req)
      
        if(response.success){
          const input = {
            id: this.tenant.id,
            payrollProvider: this.form.payrollProvider,
            customerCheckedUserRolesDateTime: new Date().toISOString()
          }
          await this.api(updateTenantDetail, {input: input})
          this.$store.dispatch('syncCurrentUserTenant', input)

          const billingContactRole = this.userRoles.find(item => item.name === "Billing Contact")
          const stripeCustomerId = this.tenant?.stripeCustomerId

          if(billingContactRole && stripeCustomerId) {
            const data = await getUserRoleLinksByUserRoleId(billingContactRole.id)
            const billingContacts = data.filter(userRoleLink => userRoleLink.user).map(userRoleLink => {
                const user = userRoleLink.user
                  return {
                    email: user.email,
                    firstName: user.firstName,
                    lastName: user.lastName
                  }
            })

            let apiName = 'stripeSetup'
            let path = '/customer/update-billing-contacts/' + stripeCustomerId
            let post = {
              body: {
                stripeCustomerId: stripeCustomerId,
                billingContacts
              }
            }
            try {
              await safeFunction(API.put)(apiName, path, post)
            } catch (e) {
              console.error('Error updating Stripe billing contacts', e)
            }
          }
        }
        this.buttonLoading = false
        this.showDialog = false
      } catch (error) {
        this.buttonLoading = false
        console.error(error)
        this.displayUserError(error)
      }
    },
    onLater() {
      this.showDialog = false;
    },
  },
};
</script>

<style scoped>

</style>
